import { z } from 'zod';

import { listSearchSchema } from '@/shared/patterns/list-search';

import { MESSAGE_GROUP_VALUES, OPERATION_STATUS_VALUES, OPERATION_TYPE_VALUES, PIX_TYPE_VALUES } from './spi.constants';

// `validateSearch` das duas rotas da Gestão SPI (U07). Leve de propósito: é JS inicial.
// Na URL da consulta ficam só os filtros **aplicados** (o que foi pesquisado); o rascunho é do formulário.

/** O roteador lê `?entryChannel=0012` como texto e `?entryChannel=12` como número: os dois voltam texto. */
const text = z
  .union([z.string(), z.number().transform(String)])
  .pipe(z.string().max(200))
  .optional()
  .catch(undefined);

const choice = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .union([z.string(), z.number().transform(String)])
    .pipe(z.enum(values))
    .optional()
    .catch(undefined);

/** Centavos como dígitos (o `CurrencyFormField` do legado grava `valor * 100`). */
const cents = z
  .union([z.string(), z.number().transform(String)])
  .pipe(z.string().regex(/^-?\d{1,15}$/))
  .optional()
  .catch(undefined);

/** `datetime-local` do legado: `YYYY-MM-DDTHH:mm` (ou com segundos), hora de parede. */
const localDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/)
  .optional()
  .catch(undefined);

export const spiConsultFilterShape = {
  messageGroup: choice(MESSAGE_GROUP_VALUES),
  operationType: choice(OPERATION_TYPE_VALUES),
  startDate: localDateTime,
  endDate: localDateTime,
  amountFrom: cents,
  amountTo: cents,
  counterpartyBankIspb: text,
  counterpartyBankIspbDisplay: text,
  bankName: text,
  operationStatus: choice(OPERATION_STATUS_VALUES),
  /** CPF/CNPJ como a pessoa digitou, com máscara (é o que o campo do legado guarda). */
  registrationNumberSearch: text,
  endToEndId: text,
  errorMessageFilter: text,
  entryChannel: text,
  pixType: choice(PIX_TYPE_VALUES),
  /** Ordem dos seletores mexidos, para o fio do legado (R3). */
  auxiliares: z
    .string()
    .regex(/^(messageGroup|operationType|bank|operationStatus|pixType)(,(messageGroup|operationType|bank|operationStatus|pixType)){0,4}$/)
    .optional()
    .catch(undefined),
};

/**
 * A consulta de operações PIX. A paginação é **local** (o legado nunca manda `page`/`rowsPerPage`
 * para a API, `SPIConsultContainer.tsx:36-41`), mas o que a pessoa está vendo fica na URL (U26).
 */
export const spiConsultSearch = listSearchSchema({
  ...spiConsultFilterShape,
  /** A pessoa pesquisou (o `filters !== undefined` do legado, `SPIManagementContext.tsx:169`). */
  consulta: z.coerce.number().pipe(z.literal(1)).optional().catch(undefined),
});
export type SpiConsultSearch = z.infer<typeof spiConsultSearch>;

/**
 * O painel: a solicitação aberta (`?solicitacao=aporte|saque`) fica na URL para o voltar fechar (U26).
 * "Mostrar/ocultar valores" **não** entra aqui: no legado é estado de sessão (`showValuesContext.tsx`,
 * `useState(true)`), sem URL e sem `localStorage`; aqui também (LGPD, trava de armazenamento).
 */
export const spiPanelSearch = z.object({
  solicitacao: z.enum(['aporte', 'saque']).optional().catch(undefined),
  /** Valor já preenchido no formulário, em centavos: é como a ferramenta do agente **prepara** (U24). */
  valor: cents,
});
export type SpiPanelSearch = z.infer<typeof spiPanelSearch>;
